import { formatNumber, PercentPipe } from '@angular/common';
import { Injectable, QueryList } from '@angular/core';
import { FormArray } from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import {
  ExtraScheduleData,
  Fixing,
  Leg,
  LegPrice,
  Payload,
  PricingLatestValuesRequests,
  ReportingCurrencyStateKey
} from '@approot/models';
import {
  LegService,
  MARKET_DATA_AUTO_REFRESH,
  PricingExtraService,
  ProductsConfigService,
  StrategyDetailService,
  UserStorageService
} from '@approot/services';
import { ScheduleUtilities, Utilities } from '@approot/utilities';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { DynamicLegAttributeComponent } from '../../dynamic-leg-attribute/dynamic-leg-attribute.component';
import { canDcdPrice } from '../utilities/dcd-utilities';
import { getAttributeControlFromKey } from '../utilities/leg-attribute-utilities';
import {
  clearTextColor,
  makeTextCustomColor,
  markAllUsingLatestValuesPending,
  markFieldAsPending,
  removePendingFromField,
  shouldLegUseWebSocket,
  usingLatestValues
} from '../utilities/leg.utilities';
import { LegFwdVolService } from './leg-fwd-vol.service';
import { LegHelperService } from './leg-helper.service';
import { LegSpotStrikeService } from './leg-spot-strike.service';

@Injectable({
  providedIn: 'root'
})
export class LegPricingService {
  public pricingSub: Subscription;
  constructor(
    private legSpotStrikeService: LegSpotStrikeService,
    private legHelperService: LegHelperService,
    private productsConfigService: ProductsConfigService,
    private percentPipe: PercentPipe,
    private strategyDetailService: StrategyDetailService,
    private legService: LegService,
    private legFwdVolService: LegFwdVolService,
    private webSocketService: GlobalWebsocketService,
    private pricingExtraService: PricingExtraService,
    private userStorageService: UserStorageService
  ) {}

  public allowPricing(): boolean {
    return (
      this.legSpotStrikeService.isStrikeValid('strike') &&
      this.legSpotStrikeService.isStrikeValid('strike1') &&
      this.legSpotStrikeService.isStrikeValid('strike2')
    );
  }

  public processExtraScheduleData(flows: ExtraScheduleData): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const cross = form.get('cross')?.value;
    Object.keys(flows).forEach(key => {
      leg.extraScheduleData[key] = { data: [], columns: [] };
      const schedule = form.get(key) as FormArray;
      const flow = flows[key];
      if (!schedule) {
        return;
      }

      leg.extraScheduleData[key].data = flow.data.map((row, index) => {
        let newRow = {};
        Object.keys(row).forEach(field => {
          let columnLabelDef = flow.columnLabels.find(
            label => label.shortName === field
          );

          if (!columnLabelDef) {
            return;
          }

          if (field === 'fixing') {
            const scheduleRow = schedule.controls[index];
            const fixingControl = scheduleRow.get('fixing');
            if (fixingControl) {
              const newValue = row[field].toFixed(
                this.productsConfigService.decimalPlacesForCross(cross)
              );
              fixingControl.setValue(newValue, { emitEvent: false });
            }
          } else if (columnLabelDef) {
            const value = row[field];
            if (!leg.extraScheduleData[key].columns.includes(columnLabelDef)) {
              leg.extraScheduleData[key].columns.push(columnLabelDef);
            }
            newRow[field] = ScheduleUtilities.formatValue(
              columnLabelDef.format,
              columnLabelDef.format === 'percentage'
                ? (value * 100).toFixed(2)
                : value,
              columnLabelDef.nDecimals,
              false
            );
          }
        });
        return newRow;
      });
    });
  }

  public dcdPriced(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const legStrategy = this.legHelperService.legStrategy;

    if (leg.missingAttributeOnPricing) {
      // make the missing attribute text orange
      makeTextCustomColor(
        leg.missingAttributeOnPricing,
        Utilities.blueTextColor,
        leg
      ); // make the text be blue
    }

    // fill in the missing attributes with values from backend
    if (leg.missingAttributeOnPricing === 'conversionRate') {
      form
        .get('conversionRate')
        .setValue(
          formatNumber(
            leg.pricedLeg.dcdResults.fairStrikeForRate,
            'en-GB',
            this.productsConfigService.decimalPlacesForCrossPipe(
              legStrategy.products[0].depositCurrency +
                legStrategy.products[0].alternativeCurrency
            )
          ).replace(/,/g, ''),
          { emitEvent: false }
        );
    } else if (leg.missingAttributeOnPricing === 'depositRate') {
      form
        .get('depositRate')
        .setValue(
          this.percentPipe
            .transform(leg.pricedLeg.dcdResults.fairRateForStrike, '1.4-4')
            .replace(/,/g, ''),
          { emitEvent: false }
        );
    } else if (leg.missingAttributeOnPricing === 'margin') {
      if (legStrategy.products[0].marginType === 'Deposit Currency') {
        form
          .get('margin')
          .setValue(
            formatNumber(
              leg.pricedLeg.dcdResults.priceDepositCurrency,
              'en-GB',
              '1.4-4'
            ) +
              ' ' +
              legStrategy.products[0].depositCurrency,
            { emitEvent: false }
          );
      } else {
        form
          .get('margin')
          .setValue(
            this.percentPipe
              .transform(
                leg.pricedLeg.dcdResults.priceYieldDepositCurrency,
                '1.4-4'
              )
              .replace(/,/g, ''),
            { emitEvent: false }
          );
      }
    }
  }

  public handlePricingResults(resp: LegPrice, resultPerLegIndex = 0): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    leg.disablePricingButton = false;
    if (resp.resultPerLeg?.[resultPerLegIndex]?.pricing?.flows) {
      // if there are additional columns to be added to the schedule for the flows
      this.processExtraScheduleData(
        resp.resultPerLeg[resultPerLegIndex].pricing.flows
      );
    }

    if (resp.resultPerLeg?.[resultPerLegIndex]?.pricing?.exercise) {
      // if there are additional columns to be added to the schedule for the flows
      this.processExtraScheduleData({
        exerciseSchedule: resp.resultPerLeg[resultPerLegIndex].pricing.exercise
      });
    }

    leg.pricedLeg = resp.resultPerLeg[resultPerLegIndex];
    if (
      this.strategyDetailService.marketDataToUse === MARKET_DATA_AUTO_REFRESH
    ) {
      leg.priceHistory.push(resp.resultPerLeg[resultPerLegIndex]); // if using auto refresh push the new price to the pricing history array
    }
    leg.loadingPrices = false; // its not fetching anymore

    if (resp.resultPerLeg[resultPerLegIndex].description) {
      leg.legDescription.description =
        resp.resultPerLeg[resultPerLegIndex].description;
      leg.legDescription.valueChanged = false;
    }

    if (leg.missingAttributeOnPricing) {
      removePendingFromField(
        form,
        leg,
        rfqExecutionSettings,
        leg.missingAttributeOnPricing
      );
    }

    if (leg.productType === 'newdcd') {
      this.dcdPriced();
    }

    if (rfqExecutionSettings.rfqLeg) {
      return;
    }
    if (!rfqExecutionSettings.directRfq) {
      this.legHelperService.onChangeInLegFn();
    }

    this.legHelperService.onChangeInLegPriceFn();
  }

  public pricingDcd(strategy: Payload, index = 0): void {
    // when pricing dcd if one of the rates/ margin are empty
    // store the attribute name as a missing attribute
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    clearTextColor(leg, leg.missingAttributeOnPricing); // remove color from text
    if (
      strategy.products[index].conversionRate === '' ||
      leg.missingAttributeOnPricing === 'conversionRate'
    ) {
      leg.missingAttributeOnPricing = 'conversionRate';
      delete strategy.products[index].conversionRate;
    } else if (
      strategy.products[index].depositRate === '' ||
      leg.missingAttributeOnPricing === 'depositRate'
    ) {
      leg.missingAttributeOnPricing = 'depositRate';
      delete strategy.products[index].depositRate;
    } else if (
      strategy.products[index].margin === '' ||
      leg.missingAttributeOnPricing === 'margin'
    ) {
      leg.missingAttributeOnPricing = 'margin';
      delete strategy.products[index].margin;
    }

    if (leg.missingAttributeOnPricing) {
      markFieldAsPending(form, leg, leg.missingAttributeOnPricing);
    }
  }

  public get getPricingStrategy(): Payload {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const reportingCurrency = this.userStorageService.getItem<string>(
      ReportingCurrencyStateKey
    ); // get the stored reporting currency
    let product = form.value;

    let task = {
      taskType: 'price',
      reportingCurrency: reportingCurrency || 'USD', // if none is set, use USD
      taskParameters: {
        output: ['Price', 'Gamma', 'Vega', 'SpotDelta']
      }
    };

    return this.legService.createServicePayload(
      this.pricingExtraService.getMarketDataTimeStamp,
      this.pricingExtraService.marketDataSpreadingRule,
      [{ product, attributes: leg.attributes }],
      task,
      leg.pricingData
    );
  }

  public priceAfterLatestValues(strategy: Payload): void {
    // handles fetching the latest value of the fields before pricing
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const liveValueRequests: PricingLatestValuesRequests = {};
    this.legSpotStrikeService.addRequestsForLatestsSpots(liveValueRequests);
    markAllUsingLatestValuesPending(leg, form, rfqExecutionSettings);
    this.legFwdVolService.addRequestsForLatestsImpliedVols(
      '',
      liveValueRequests
    );
    this.legFwdVolService.addRequestsForLatestsImpliedVols(
      '1',
      liveValueRequests
    );
    this.legFwdVolService.addRequestsForLatestsImpliedVols(
      '2',
      liveValueRequests
    );
    this.legSpotStrikeService.addShortcutStrikesRequest(liveValueRequests);
    this.legFwdVolService.addForwardPointsRequests(liveValueRequests, strategy);
    forkJoin(liveValueRequests as Record<string, Observable<any>>).subscribe({
      next: _ => {
        markAllUsingLatestValuesPending(leg, form, rfqExecutionSettings, false);

        this.priceLeg(); // price the leg after the spots have been updated
      },
      error: _ => this.onGetLatestValuesError()
    });
    leg.disablePricingButton = true;
  }

  public priceLeg(): void {
    const leg = this.legHelperService.leg;
    if (this.allowPricing) {
      const strategy = this.getPricingStrategy;
      delete strategy.products[0][leg.missingAttributeOnPricing];
      leg.disablePricingButton = true;
      this.legHelperService.legStrategy = strategy;
      if (shouldLegUseWebSocket(leg)) {
        this.webSocketService.sendMessage({
          action: `${API_PREFIX_V2}pricing`,
          payload: strategy,
          uuid: this.legHelperService.streamingWebsocketUUID
        });
      } else {
        this.pricingSub = this.legService.priceLeg(strategy).subscribe({
          next: (resp: LegPrice) => this.handlePricingResults(resp),
          error: () => this.pricingFailed()
        });
        this.legHelperService.subSink.sink = this.pricingSub;
      }
    }
  }

  public pricingFailed(): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    leg.disablePricingButton = false;
    leg.loadingPrices = false; // its not fetching anymore
    leg.pricingErrorMsg = 'Failed to price leg'; // if there is an error fetching the results
    if (leg.missingAttributeOnPricing)
      removePendingFromField(
        form,
        leg,
        rfqExecutionSettings,
        leg.missingAttributeOnPricing
      );
  }

  private onGetLatestValuesError(): void {
    // go through all of the attributes that should be refreshed on pricings
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    leg.disablePricingButton = false;
    markAllUsingLatestValuesPending(leg, form, rfqExecutionSettings, false);
  }

  public price(doNotShowSpinner = false): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    if (!doNotShowSpinner) {
      leg.loadingPrices = true;
    }
    leg.pricingErrorMsg = '';

    // if we have requiredFixings which is needed if:
    // - schedule is calculated with a fixing date in the past
    // - or, product has expiry date in the past
    // then choose the fixings that match with the chosen fixing source (BBG_1500, TKFE, etc)
    // default to use WMR_1600 if there is expiryDate (cutOff) instead of schedules/fixing-dates (fixingSource)
    if (
      this.legHelperService.requiredFixings &&
      (form.get('fixingSource') || form.get('cutOff'))
    ) {
      leg.pricingData.fixings = this.getFixings();
    }

    let strategy = this.getPricingStrategy;

    if (form.get('position')) {
      leg.position = form.get('position').value;
    }

    if (leg.productType === 'newdcd') {
      this.pricingDcd(strategy);
    }

    if (usingLatestValues(leg)) {
      // if either the spot or second spot have been generated from a service call
      // we need to update them to the latest values
      this.priceAfterLatestValues(strategy);
    } else {
      this.priceLeg();
    }
  }

  // get fixings based on current fixing source and the scheduled fixing dates in the past
  public getFixings(): Fixing[] {
    // {
    // shortName is dependent on whether there is a fixingSource or not
    const form = this.legHelperService.form;
    const shortName = form.get('fixingSource') ? 'fixingSource' : 'cutOff';
    let currentSource;
    if (shortName === 'fixingSource') {
      currentSource =
        this.productsConfigService.cutOffTimesAndFixingSources.fixingSources.filter(
          y => y.longName === form.get(shortName)?.value
        )[0];
    } else {
      currentSource =
        this.productsConfigService.cutOffTimesAndFixingSources.cutOffs.filter(
          y => y.longName === form.get(shortName)?.value
        )[0];
    }
    // NOTE!! we should be returning the required spot-fixings filtered by fixingSource
    // return this.requiredFixings.filter((f: Fixing) => {
    //   return f.fixingSource === currentSource.code;
    // });
    // instead for now, we will attach the necessary fixingSource property and set to the SpotMid
    this.legHelperService.requiredFixings.forEach(rf => {
      rf['fixingSource'] = currentSource.code;
    });
    return this.legHelperService.requiredFixings;
  }

  public repriceDcdChange(
    fieldName: string,
    legComponents: QueryList<DynamicLegAttributeComponent>
  ): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    if (
      getAttributeControlFromKey(fieldName, leg.attributesAsFormControls)
        .readOnly
    ) {
      return;
    }

    const createdCross = this.productsConfigService.getCrossFromCurrencies(
      form.get('depositCurrency').value,
      form.get('alternativeCurrency').value
    );
    if (fieldName === 'conversionRate' && createdCross) {
      // on blur format the field
      form
        .get('conversionRate')
        .setValue(
          this.productsConfigService.crossDecimalFormatter(
            form.get('conversionRate').value,
            createdCross
          ),
          { emitValue: false }
        );
    }
    // only allow pricing of leg when form is valid and only 2 of the 3 attributes have values
    if (
      form.valid &&
      canDcdPrice(rfqExecutionSettings, form, leg.loadingPrices, leg)
    ) {
      this.onPricingSubmit(legComponents);
    }
  }

  public onPricingSubmit(
    legComponents: QueryList<DynamicLegAttributeComponent>
  ): void {
    const leg = this.legHelperService.leg;
    const form = this.legHelperService.form;
    if (
      !form?.valid ||
      leg.disablePricingButton ||
      !this.legHelperService.allLegsValid(legComponents)
    ) {
      return;
    }
    this.clearPricingData(leg);
    this.price();
  }

  public clearPricingData(leg: Leg): void {
    leg.pricingData = {
      treasuryRates: [],
      fixings: [],
      marketData: []
    };
  }
}
